import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Clock, Loader2, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

/**
 * Admin → Portal-Zustimmungs-Log (AGB & Datenschutz der kundenseitigen
 * Mobil-/Public-Portale). Revisionssicherer DSGVO-Nachweis (Art. 7 DSGVO).
 */
export default function AdminPortalConsentLogPage() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [labels, setLabels] = useState({});
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        if (!authLoading && user?.role !== "admin") navigate("/");
    }, [user, authLoading, navigate]);

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (filter) params.portal = filter;
            const { data } = await api.get("/admin/portal-consent-log", { params });
            setItems(data.items || []);
            setCounts(data.counts || {});
            setLabels(data.labels || {});
            setTotal(data.total || 0);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Consent-Log nicht ladbar");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.role === "admin") load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, filter]);

    if (authLoading || user?.role !== "admin") {
        return (
            <div className="flex items-center justify-center min-h-[40vh]">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    const portalKeys = Object.keys(labels);

    return (
        <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-portal-consent-page">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <ShieldCheck className="w-7 h-7 text-[#FFCC00]" />
                    <div>
                        <h1 className="font-display text-2xl font-bold">Portal-Zustimmungen (AGB &amp; Datenschutz)</h1>
                        <p className="text-sm text-gray-600">
                            Revisionssicherer DSGVO-Nachweis der Kunden-Bestätigungen in den Mobil-/Public-Portalen — insgesamt {total} Einträge.
                        </p>
                    </div>
                </div>
                <Link to="/admin/dealers" className="inline-flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-[#111827]">
                    <ArrowLeft className="w-4 h-4" /> Admin-Übersicht
                </Link>
            </div>

            <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="portal-consent-filters">
                <div className="text-xs uppercase tracking-wider font-bold text-gray-500 mr-1">Portal:</div>
                <button
                    onClick={() => setFilter("")}
                    className={`text-xs px-2 py-1 rounded-sm font-bold uppercase tracking-wider ${filter === "" ? "bg-[#FFCC00] text-[#111827]" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                    data-testid="filter-all"
                >
                    Alle ({total})
                </button>
                {portalKeys.map((k) => (
                    <button
                        key={k}
                        onClick={() => setFilter(k)}
                        className={`text-xs px-2 py-1 rounded-sm font-bold ${filter === k ? "bg-[#FFCC00] text-[#111827]" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                        data-testid={`filter-${k}`}
                    >
                        {labels[k]} ({counts[k] ?? 0})
                    </button>
                ))}
                <Button onClick={load} size="sm" variant="outline" className="rounded-sm text-xs ml-auto" data-testid="portal-consent-reload">
                    Neu laden
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-8 text-center" data-testid="portal-consent-empty">
                    <ShieldCheck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Noch keine Zustimmungen im gewählten Filter.</p>
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
                    <table className="w-full text-sm" data-testid="portal-consent-table">
                        <thead className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-600">
                            <tr>
                                <th className="text-left px-4 py-3">Zeit</th>
                                <th className="text-left px-4 py-3">Portal</th>
                                <th className="text-left px-4 py-3">AGB</th>
                                <th className="text-left px-4 py-3">Datenschutz</th>
                                <th className="text-left px-4 py-3">Token</th>
                                <th className="text-left px-4 py-3">IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c, i) => (
                                <tr key={i} className="border-b border-gray-100 last:border-b-0 hover:bg-[#FFFBEB]/40" data-testid="portal-consent-row">
                                    <td className="px-4 py-3 font-mono-num text-[11px] text-gray-700">
                                        <Clock className="inline-block w-3 h-3 text-gray-400 mr-1" />
                                        {c.created_at ? new Date(c.created_at).toLocaleString("de-DE") : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 font-bold text-[10px]">{c.portal_label || c.portal}</Badge>
                                    </td>
                                    <td className="px-4 py-3">
                                        {c.agb ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
                                    </td>
                                    <td className="px-4 py-3">
                                        {c.datenschutz ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-500" />}
                                    </td>
                                    <td className="px-4 py-3 text-[11px] font-mono-num text-gray-500 truncate max-w-[160px]" title={c.token || ""}>{c.token || "—"}</td>
                                    <td className="px-4 py-3 text-[11px] font-mono-num text-gray-500">{c.ip || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
